/**
 * Alle Creeps von DUELINE (Masterprompt Abschnitte 65-68).
 *
 * Wirtschaftswerte (cost, incomeGain, armyPoints, portalDamage) sind VERBINDLICH.
 *
 * Abgewichen wird davon nur auf ausdrueckliche Produktentscheidung. Stand
 * 29.09.2026 betrifft das zwei Spalten:
 *
 *   cost        gesenkt in v0.45, v0.50 - eine Welle bestand aus 1,48 Creeps.
 *   incomeGain  gesenkt auf 20 Prozent in v0.51 - das Income wuchs um 40
 *               Prozent JE MINUTE, und ueber die Haelfte des gesamten Goldes
 *               kam in den letzten zwei Minuten herein. Siehe BALANCE.md,
 *               Abschnitt "Die Wirtschaftskurve".
 *
 *   armyPoints  erhoeht fuer Runner, Raider und Phase Runner am 05.10.2026.
 *   incomeGain  auf 0 fuer Runner, Raider, Phase Runner und Doom Runner am
 *               05.10.2026 - siehe unten.
 *   cost        bei den drei Bossen verdoppelt am 05.10.2026 - siehe unten.
 *
 * Die Bosse (Produktentscheidung vom 05.10.2026): Sie waren rechnerisch
 * nicht zu toeten. Ein Titan brauchte 58 Sekunden, bis Hero UND alle
 * Defender ihn umgehauen hatten - fuer den Weg durch die Lane brauchte er
 * 40. Er kam also immer an, auch wenn alles nur auf ihn schoss.
 *
 * Leben rund 40 Prozent runter (Titan 16000 -> 9500, Warlord 14000 ->
 * 8500, Broodmother 12000 -> 7500): Damit faellt ein Boss nach etwa 30
 * Sekunden, wenn sich alles auf ihn wirft. Er bleibt ein Ereignis, das
 * alles andere stehen laesst, ist aber zu schaffen.
 *
 * Dafuer kostet er das Doppelte: Ein Boss soll eine Ansage sein, die man
 * sich leistet, kein regelmaessiger Posten in der Welle.
 *
 * Die Laeufer (Produktentscheidung vom 05.10.2026): Runner, Phase Runner und
 * Doom Runner laufen mit portal_focus an Hero und Defendern vorbei zum
 * Anchor, der Raider ignoriert mit hero_focus den Anchor und jagt den Hero.
 * Beide Arten umgehen das, wofuer die Wirtschaft da ist - sie kaempfen nicht
 * um die Lane, sie gehen daran vorbei. Deshalb geben sie kein Income mehr und
 * kosten mehr Army Points.
 *
 *   cost        plus 50 Prozent fuer ALLE Creeps am 10.10.2026 - siehe
 *               unten.
 *
 * Die Preiserhoehung (Produktentscheidung vom 10.10.2026): Alle 27 Preise
 * mal 1,5, kaufmaennisch gerundet. Sie gehoert zusammen mit dem laengeren
 * Income-Takt (15 statt 12 Sekunden) und dem gesenkten Startincome (200
 * statt 220) - drei Griffe an derselben Schraube.
 *
 * Wichtig dabei: incomeGain bleibt, wie er war. Ein Creep kostet also die
 * Haelfte mehr und bringt dasselbe Einkommen - je ausgegebenem Gold waechst
 * das Income damit um ein Drittel langsamer. Das ist Absicht und der
 * eigentliche Hebel dieser Aenderung.
 *
 * portalDamage ist unangetastet.
 * Alle Kampfwerte sind TUNING_PLACEHOLDER und duerfen frei angepasst werden.
 *
 * Balanceaenderungen sollen ausschliesslich hier passieren - nicht im Spielcode.
 */

import type { CreepDefinition } from './creep-types.js';

/*
 * Kampfschaden aller Creeps auf 90 Prozent (Produktentscheidung vom
 * 30.09.2026). Leben und Portalschaden bleiben unveraendert - die Creeps
 * sollen genauso viel aushalten und genauso wehtun, wenn sie ankommen. Sie
 * sollen nur nicht mehr alles auf dem Weg dorthin niedermaehen.
 *
 * Hintergrund: Groessere Wellen brauchten einen Ausgleich, und das Portalleben
 * steht fest bei 100 (siehe match.ts). Gemessen, Matchdauer bei Portal 100:
 *
 *   Kampfschaden   mit altem Bot   mit ausbauendem Bot
 *   100 %           7,6 Min        11,2 Min
 *    90 %           -              12,2 Min   <- hier
 *    80 %           -              14,0 Min, aber 12 % Timeouts
 *    70 %           8,6 Min        -
 *    50 %          12,0 Min        -
 *    35 %          18,7 Min, 56 % Timeouts
 *
 * Die zweite Spalte ist die eigentliche Geschichte: Der Testgegner hat seine
 * Verteidiger nie ausgebaut. Das allein brachte 3,6 Minuten - mehr als eine
 * Halbierung des Schadens. Deshalb reichen hier 10 Prozent weniger statt 50.
 *
 * Unter 80 Prozent kippt es: Dann kommt keine Seite mehr durch, und die
 * Matches laufen in die Zeitgrenze.
 */
export const CREEPS: readonly CreepDefinition[] = [
  // ---------------------------------------------------------------- TIER I
  {
    id: 'minion',
    name: 'Minion',
    tier: 1,
    cost: 11,
    incomeGain: 1,
    armyPoints: 1,
    portalDamage: 1,
    maxHp: 180,
    damage: 11,
    attackSpeed: 1.0,
    moveSpeed: 8,
    armor: 0,
    magicResistance: 0,
    aggroRange: 12,
    leashRange: 25,
    attackRange: 2,
    tags: ['melee'],
    specialBehaviour: 'none',
  },
  {
    id: 'brute',
    name: 'Brute',
    tier: 1,
    cost: 29,
    incomeGain: 2,
    armyPoints: 3,
    portalDamage: 1,
    maxHp: 520,
    damage: 20,
    attackSpeed: 0.8,
    moveSpeed: 6.5,
    armor: 3,
    magicResistance: 0,
    aggroRange: 12,
    leashRange: 25,
    attackRange: 2,
    tags: ['melee', 'tank'],
    specialBehaviour: 'none',
  },
  {
    id: 'runner',
    name: 'Runner',
    tier: 1,
    cost: 26,
    incomeGain: 0,
    armyPoints: 3,
    portalDamage: 1,
    maxHp: 75,
    damage: 7,
    attackSpeed: 1.0,
    moveSpeed: 10,
    armor: 0,
    magicResistance: 0,
    aggroRange: 6,
    leashRange: 10,
    attackRange: 2,
    tags: ['melee', 'fast'],
    specialBehaviour: 'portal_focus',
  },
  /*
   * Raider - der Jaeger (Produktentscheidung vom 27.09.2026).
   *
   * Vorher war er ein zweiter Minion mit mehr Leben: Pro Army Point war der
   * Minion bei Leben, Schaden, Income, Portalschaden UND Preis besser - es gab
   * schlicht keinen Grund, ihn zu waehlen.
   *
   * Jetzt ist er das Gegenstueck zum Runner: Der Runner ignoriert den Hero und
   * laeuft zum Portal, der Raider ignoriert das Portal und laeuft zum Hero.
   * Beide waehlen ihr eigenes Ziel - deshalb stehen beide in CENTER.
   *
   * Warum das nicht unfair ist: Ein Hero laeuft 15 bis 17, der Raider 9. Er
   * holt niemanden ein, der weglaeuft. Er trifft nur den, der stehen bleibt
   * und farmt - er kostet also Zeit, nicht Leben.
   *
   * Ein Army Point, dafuer teuer in Gold: Man kann ihn mitnehmen, ohne die
   * Welle zu opfern, aber das Gold bremst.
   */
  {
    id: 'raider',
    name: 'Raider',
    tier: 1,
    cost: 29,
    incomeGain: 0,
    armyPoints: 3,
    portalDamage: 1,
    maxHp: 200,
    damage: 27,
    attackSpeed: 0.9,
    moveSpeed: 9,
    armor: 0,
    magicResistance: 0,
    // Weite Sicht: Er soll den Hero von der anderen Lane-Seite aus ansteuern.
    aggroRange: 40,
    // Er folgt weiter als andere - aber eben nicht endlos.
    leashRange: 30,
    attackRange: 2,
    tags: ['melee', 'fast'],
    specialBehaviour: 'hero_focus',
  },
  {
    id: 'archer',
    name: 'Archer',
    tier: 1,
    cost: 26,
    incomeGain: 2,
    armyPoints: 2,
    portalDamage: 1,
    maxHp: 200,
    damage: 14,
    attackSpeed: 0.9,
    moveSpeed: 7.5,
    armor: 0,
    magicResistance: 0,
    aggroRange: 14,
    leashRange: 22,
    attackRange: 12,
    tags: ['ranged'],
    specialBehaviour: 'none',
  },
  {
    id: 'trader',
    name: 'Trader',
    tier: 1,
    cost: 26,
    incomeGain: 4,
    armyPoints: 1,
    portalDamage: 1,
    maxHp: 240,
    damage: 4,
    attackSpeed: 0.6,
    moveSpeed: 7,
    armor: 0,
    magicResistance: 0,
    aggroRange: 6,
    leashRange: 12,
    attackRange: 2,
    tags: ['economy'],
    specialBehaviour: 'grants_bonus_income',
  },

  // --------------------------------------------------------------- TIER II
  {
    id: 'iron_guard',
    name: 'Iron Guard',
    tier: 2,
    cost: 29,
    incomeGain: 3,
    armyPoints: 4,
    portalDamage: 1,
    maxHp: 900,
    damage: 25,
    attackSpeed: 0.8,
    moveSpeed: 6.5,
    armor: 6,
    magicResistance: 0,
    aggroRange: 12,
    leashRange: 25,
    attackRange: 2,
    tags: ['melee', 'tank'],
    specialBehaviour: 'none',
  },
  {
    id: 'rune_guard',
    name: 'Rune Guard',
    tier: 2,
    cost: 29,
    incomeGain: 3,
    armyPoints: 4,
    portalDamage: 1,
    maxHp: 820,
    damage: 23,
    attackSpeed: 0.8,
    moveSpeed: 6.5,
    armor: 1,
    magicResistance: 40,
    aggroRange: 12,
    leashRange: 25,
    attackRange: 2,
    tags: ['melee', 'tank'],
    specialBehaviour: 'none',
  },
  {
    id: 'berserker',
    name: 'Berserker',
    tier: 2,
    cost: 23,
    incomeGain: 3,
    armyPoints: 3,
    portalDamage: 2,
    maxHp: 560,
    damage: 31,
    attackSpeed: 1.3,
    moveSpeed: 9,
    armor: 1,
    magicResistance: 0,
    aggroRange: 12,
    leashRange: 25,
    attackRange: 2,
    tags: ['melee'],
    specialBehaviour: 'none',
  },
  {
    id: 'medic',
    name: 'Medic',
    tier: 2,
    cost: 29,
    incomeGain: 3,
    // ABWEICHUNG vom Masterprompt, 3 -> 5 (Produktentscheidung vom
    // 09.10.2026). Begruendung an HEAL_ABILITIES.
    armyPoints: 5,
    portalDamage: 1,
    maxHp: 480,
    damage: 9,
    attackSpeed: 0.7,
    moveSpeed: 7.5,
    armor: 0,
    magicResistance: 10,
    aggroRange: 12,
    leashRange: 20,
    attackRange: 10,
    tags: ['ranged', 'support'],
    specialBehaviour: 'heals_nearby_allies',
  },
  {
    id: 'longbow',
    name: 'Longbow',
    tier: 2,
    cost: 26,
    incomeGain: 3,
    armyPoints: 3,
    portalDamage: 1,
    maxHp: 380,
    damage: 27,
    attackSpeed: 0.8,
    moveSpeed: 7,
    armor: 0,
    magicResistance: 0,
    aggroRange: 18,
    leashRange: 24,
    attackRange: 16,
    tags: ['ranged'],
    specialBehaviour: 'none',
  },
  {
    id: 'phase_runner',
    name: 'Phase Runner',
    tier: 2,
    cost: 30,
    incomeGain: 0,
    armyPoints: 4,
    portalDamage: 2,
    maxHp: 140,
    damage: 11,
    attackSpeed: 1.0,
    moveSpeed: 11.5,
    armor: 0,
    magicResistance: 10,
    aggroRange: 5,
    leashRange: 8,
    attackRange: 2,
    tags: ['melee', 'fast'],
    specialBehaviour: 'portal_focus',
  },
  {
    id: 'merchant_ii',
    name: 'Merchant II',
    tier: 2,
    cost: 65,
    incomeGain: 8,
    armyPoints: 2,
    portalDamage: 1,
    maxHp: 520,
    damage: 5,
    attackSpeed: 0.6,
    moveSpeed: 7,
    armor: 1,
    magicResistance: 0,
    aggroRange: 6,
    leashRange: 12,
    attackRange: 2,
    tags: ['economy'],
    specialBehaviour: 'grants_bonus_income',
  },

  // -------------------------------------------------------------- TIER III
  {
    id: 'commander',
    name: 'Commander',
    tier: 3,
    cost: 68,
    incomeGain: 6,
    armyPoints: 5,
    portalDamage: 2,
    maxHp: 1500,
    damage: 43,
    attackSpeed: 0.9,
    moveSpeed: 7,
    armor: 8,
    magicResistance: 10,
    aggroRange: 14,
    leashRange: 25,
    attackRange: 2,
    tags: ['melee', 'support'],
    specialBehaviour: 'buffs_nearby_allies',
  },
  {
    id: 'necromancer',
    name: 'Necromancer',
    tier: 3,
    cost: 75,
    incomeGain: 6,
    armyPoints: 5,
    portalDamage: 2,
    maxHp: 1100,
    damage: 32,
    attackSpeed: 0.8,
    moveSpeed: 7,
    armor: 2,
    magicResistance: 25,
    aggroRange: 16,
    leashRange: 24,
    attackRange: 13,
    tags: ['ranged', 'summoner'],
    specialBehaviour: 'summons_minions',
  },
  {
    id: 'splitter',
    name: 'Splitter',
    tier: 3,
    cost: 51,
    incomeGain: 5,
    armyPoints: 4,
    portalDamage: 1,
    maxHp: 950,
    damage: 27,
    attackSpeed: 0.9,
    moveSpeed: 7.5,
    armor: 3,
    magicResistance: 0,
    aggroRange: 12,
    leashRange: 25,
    attackRange: 2,
    tags: ['melee'],
    specialBehaviour: 'splits_on_death',
  },
  {
    id: 'hunter',
    name: 'Hunter',
    tier: 3,
    cost: 59,
    incomeGain: 4,
    armyPoints: 4,
    portalDamage: 2,
    maxHp: 900,
    damage: 50,
    attackSpeed: 0.9,
    moveSpeed: 9,
    armor: 2,
    magicResistance: 0,
    aggroRange: 16,
    leashRange: 26,
    attackRange: 11,
    tags: ['ranged'],
    specialBehaviour: 'none',
  },
  {
    id: 'juggernaut',
    name: 'Juggernaut',
    tier: 3,
    cost: 90,
    incomeGain: 7,
    armyPoints: 6,
    portalDamage: 2,
    maxHp: 2600,
    damage: 54,
    attackSpeed: 0.7,
    moveSpeed: 5.5,
    armor: 12,
    magicResistance: 10,
    aggroRange: 12,
    leashRange: 25,
    attackRange: 3,
    tags: ['melee', 'tank'],
    specialBehaviour: 'none',
  },
  {
    id: 'siege_beast',
    name: 'Siege Beast',
    tier: 3,
    cost: 72,
    incomeGain: 5,
    armyPoints: 5,
    portalDamage: 4,
    maxHp: 2000,
    damage: 41,
    attackSpeed: 0.6,
    moveSpeed: 5,
    armor: 10,
    magicResistance: 0,
    aggroRange: 10,
    leashRange: 18,
    attackRange: 3,
    tags: ['melee', 'siege'],
    specialBehaviour: 'none',
  },
  {
    id: 'investor_iii',
    name: 'Investor III',
    tier: 3,
    cost: 165,
    incomeGain: 19,
    armyPoints: 3,
    portalDamage: 1,
    maxHp: 1200,
    damage: 9,
    attackSpeed: 0.6,
    moveSpeed: 7,
    armor: 3,
    magicResistance: 10,
    aggroRange: 6,
    leashRange: 12,
    attackRange: 2,
    tags: ['economy'],
    specialBehaviour: 'grants_bonus_income',
  },

  // --------------------------------------------------------------- TIER IV
  {
    id: 'colossus',
    name: 'Colossus',
    tier: 4,
    cost: 357,
    incomeGain: 11,
    armyPoints: 8,
    portalDamage: 3,
    maxHp: 5200,
    damage: 99,
    attackSpeed: 0.7,
    moveSpeed: 5.5,
    armor: 18,
    magicResistance: 15,
    aggroRange: 12,
    leashRange: 25,
    attackRange: 3,
    tags: ['melee', 'tank'],
    specialBehaviour: 'none',
  },
  {
    id: 'void_priest',
    name: 'Void Priest',
    tier: 4,
    cost: 320,
    incomeGain: 12,
    // ABWEICHUNG vom Masterprompt, 7 -> 9 (Produktentscheidung vom
    // 09.10.2026). Begruendung an HEAL_ABILITIES.
    armyPoints: 9,
    portalDamage: 2,
    maxHp: 3200,
    damage: 72,
    attackSpeed: 0.8,
    moveSpeed: 7,
    armor: 4,
    magicResistance: 45,
    aggroRange: 16,
    leashRange: 24,
    attackRange: 13,
    tags: ['ranged', 'support'],
    specialBehaviour: 'heals_nearby_allies',
  },
  {
    id: 'doom_runner',
    name: 'Doom Runner',
    tier: 4,
    cost: 383,
    incomeGain: 0,
    armyPoints: 10,
    portalDamage: 3,
    maxHp: 1190,
    damage: 36,
    attackSpeed: 1.0,
    moveSpeed: 13,
    armor: 4,
    magicResistance: 20,
    aggroRange: 5,
    leashRange: 8,
    attackRange: 2,
    tags: ['melee', 'fast'],
    specialBehaviour: 'portal_focus',
  },
  {
    id: 'titan',
    name: 'Titan',
    tier: 4,
    cost: 1635,
    incomeGain: 20,
    armyPoints: 12,
    portalDamage: 6,
    maxHp: 9500,
    damage: 198,
    attackSpeed: 0.7,
    moveSpeed: 5,
    armor: 25,
    magicResistance: 25,
    aggroRange: 14,
    leashRange: 26,
    attackRange: 4,
    tags: ['melee', 'tank', 'boss'],
    specialBehaviour: 'none',
  },
  {
    id: 'broodmother',
    name: 'Broodmother',
    tier: 4,
    cost: 1530,
    incomeGain: 22,
    armyPoints: 12,
    portalDamage: 5,
    maxHp: 7500,
    damage: 135,
    attackSpeed: 0.9,
    moveSpeed: 6,
    armor: 18,
    magicResistance: 20,
    aggroRange: 14,
    leashRange: 26,
    attackRange: 3,
    tags: ['melee', 'summoner', 'boss'],
    specialBehaviour: 'summons_minions',
  },
  {
    id: 'warlord',
    name: 'Warlord',
    tier: 4,
    cost: 1584,
    incomeGain: 21,
    armyPoints: 12,
    portalDamage: 6,
    maxHp: 8500,
    damage: 180,
    attackSpeed: 0.8,
    moveSpeed: 6,
    armor: 20,
    magicResistance: 20,
    aggroRange: 14,
    leashRange: 26,
    attackRange: 3,
    tags: ['melee', 'support', 'boss'],
    specialBehaviour: 'buffs_nearby_allies',
  },
  {
    id: 'tycoon',
    name: 'Tycoon',
    tier: 4,
    cost: 510,
    incomeGain: 60,
    armyPoints: 4,
    portalDamage: 1,
    maxHp: 4000,
    damage: 14,
    attackSpeed: 0.6,
    moveSpeed: 7,
    armor: 6,
    magicResistance: 15,
    aggroRange: 6,
    leashRange: 12,
    attackRange: 2,
    tags: ['economy'],
    specialBehaviour: 'grants_bonus_income',
  },
];

/** Schneller Zugriff auf einen Creep ueber seine ID. */
export const CREEPS_BY_ID: ReadonlyMap<string, CreepDefinition> = new Map(
  CREEPS.map((creep) => [creep.id, creep]),
);

/** Liefert die Definition eines Creeps oder undefined, wenn die ID unbekannt ist. */
export function getCreep(id: string): CreepDefinition | undefined {
  return CREEPS_BY_ID.get(id);
}

/**
 * Zusatzschaden, den jeder von der Broodmother beschworene Add am Portal verursacht
 * (Masterprompt Abschnitt 68).
 */
export const BROODMOTHER_ADD_PORTAL_DAMAGE = 1;

/** Maximal erlaubte Anzahl Boss-Creeps pro Welle (Masterprompt Abschnitt 68). */
export const MAX_BOSSES_PER_WAVE = 1;

/**
 * Schadensfaktor des Archers gegen Creeps mit dem Tag "fast"
 * (Produktentscheidung vom 24.09.2026, umgezogen am 08.10.2026).
 *
 * Der Konter gegen die Laeufer. Sie ignorieren jede Aggro und laufen am
 * Kampf vorbei zum Anchor; nur wer sie unterwegs trifft, haelt sie auf.
 *
 * Bis zur gemeinsamen Lane trug diese Regel der Archer-DEFENDER. Mit dem
 * Defense System faellt er weg, und die Regel wandert auf den
 * Archer-CREEP (GAME_DESIGN Abschnitt 12a). Sie bleibt dieselbe, sie
 * wechselt nur den Traeger - und passt damit zur Kernidee: Wer Laeufer
 * erwartet, nimmt Fernkaempfer in seine eigene Welle.
 *
 * TUNING_PLACEHOLDER.
 */
export const ARCHER_DAMAGE_MULTIPLIER_VS_FAST = 2;

/**
 * Wer den Konter gegen schnelle Creeps traegt
 * (Produktentscheidung vom 08.10.2026).
 *
 * Bis dahin war es der Archer allein - ein Creep aus Tier I mit 200
 * Leben und 14 Schaden. Gegen einen Doom Runner in Minute 15 stand damit
 * als einziges Gegenmittel eine Einheit aus Minute 1.
 *
 * Jetzt traegt ihn die ganze SCHUETZENREIHE: Archer (I), Longbow (II),
 * Hunter (III). Damit hat jedes Tier eine Antwort auf seine Laeufer, und
 * die Regel bleibt einfach zu merken - Schuetzen schiessen Laeufer.
 *
 * Der Void Priest (IV) steht bewusst NICHT dabei: Er ist ein Heiler mit
 * Reichweite, kein Schuetze. Haette er den Bonus, waere der Konter ein
 * Nebeneffekt der Heilung statt eine eigene Entscheidung.
 *
 * Die Liste steht hier und nicht als Tag am Creep: Ein Tag stuende auf
 * der Karte, und ob der Konter dort auftauchen soll, ist noch nicht
 * entschieden.
 */
export const FAST_COUNTER_CREEPS: readonly string[] = ['archer', 'longbow', 'hunter'];

/**
 * Wie stark ein Creep den gegnerischen HERO gegenueber gegnerischen Creeps
 * bevorzugt (Produktentscheidung vom 08.10.2026).
 *
 * Kleiner als 1, also schwaecher als jeder Creep: Steht ein gegnerischer
 * Creep in Reichweite, gilt ihm der Angriff. Der Hero wird nur dann zum
 * Ziel, wenn gerade kein Creep da ist.
 *
 * Der Grund steht in GAME_DESIGN Abschnitt 12: "Heroes duerfen nicht
 * automatisch zum Dauerfokus aller Creeps werden." Ohne diese Vorliebe
 * zoege ein Hero, der sich in den Kampf stellt, sofort die ganze
 * gegnerische Welle auf sich - und das Spiel waere ein MOBA.
 *
 * Die Endfassung der Regel ("nur wenn er selbst eingreift") braucht das
 * Retaliation Window und kommt mit dem Leash-Schritt. TUNING_PLACEHOLDER.
 */
export const HERO_THREAT_FOR_CREEPS = 0.4;

/**
 * Wie stark ein Creep einen durchlaufenden LAEUFER gegenueber einem
 * kaempfenden Creep bevorzugt (Produktentscheidung vom 08.10.2026).
 *
 * Laeufer (portal_focus) kaempfen nie - sie rennen am Gefecht vorbei zum
 * Anchor. Angreifbar sind sie trotzdem, sonst gaebe es keinen Konter gegen
 * sie (GAME_DESIGN Abschnitt 12a).
 *
 * Kleiner als 1, damit die Frontline stehen bleibt: Wer gerade im Gefecht
 * steht, laesst sie durch. Wer frei ist - typischerweise die hintere Reihe
 * mit den Archern - nimmt sie aufs Korn. Waeren sie gleichwertig, rannten
 * die Nahkaempfer ihnen bis zur Leash hinterher und stuenden dann nicht
 * mehr dort, wo sie gebraucht werden.
 *
 * Hoeher als HERO_THREAT_FOR_CREEPS: Ein Laeufer kostet Anchorleben, der
 * Hero nicht. TUNING_PLACEHOLDER.
 */
export const RUNNER_THREAT_FOR_CREEPS = 0.7;
